import {
  focusSectionWithPath,
  getSectionIdFromPath,
} from "@blacknut/spatialnav-sdk/dist";
import React, {
  ComponentType,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  KeyboardButtonAttributes,
  KeyboardButtonTheme,
  KeyboardLayoutObject,
  KeyboardReactInterface,
  SimpleKeyboard,
} from "react-simple-keyboard";
import ModalBase from "../ModalBase/ModalBase";
import styles from "./styles.module.scss";
declare type KeyboardType = "numeric" | "default";
let KeyboardFactory: ComponentType<KeyboardReactInterface["options"]>;
const KeyboardFactoryWrapper = (props: KeyboardReactInterface["options"]) => {
  if (!KeyboardFactory) {
    KeyboardFactory = lazy<ComponentType<KeyboardReactInterface["options"]>>(
      () => {
        return import("react-simple-keyboard");
      }
    );
  }
  return <KeyboardFactory {...props} />;
};

export const getLayout = (
  type: KeyboardType = "default"
): KeyboardLayoutObject => {
  switch (type) {
    case "numeric": {
      return {
        default: ["1 2 3 4 5 6 7 8 9 0 {bksp} {enter}"],
      };
    }
    default:
      return {
        default: [
          "` 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} q w e r t y u i o p [ ] \\",
          "{lock} a s d f g h j k l ; ' {enter}",
          "{shift} z x c v b n m , . / {shift}",
          ".com @ {space}",
        ],
        shift: [
          "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P { } |",
          '{lock} A S D F G H J K L : " {enter}',
          "{shift} Z X C V B N M < > ? {shift}",
          ".com @ {space}",
        ],
      };
  }
};

const makeButtonAttributes = (type?: KeyboardType, layout = "default") => {
  const buttonAttributes: KeyboardButtonAttributes[] = [];
  const buttonThemes: KeyboardButtonTheme[] = [];
  const sectionId = getSectionIdFromPath("/modal");
  getLayout(type)[layout].forEach((value) => {
    const allBtns = value.split(" ");

    // insert tab index
    allBtns.forEach((value) => {
      buttonAttributes.push({
        attribute: "tabIndex",
        value: "-1",
        buttons: value,
      });
      buttonThemes.push(
        { class: styles.button, buttons: value },
        { class: "focusable", buttons: value },
        { class: sectionId, buttons: value }
      );
    });
  });
  return {
    theme: buttonThemes,
    attributes: buttonAttributes,
  };
};

const VirtualKeyboardModal = ({
  onClose,
  value,
  setValue,
  type,
}: {
  onClose?: () => void;
  value?: string;
  setValue?: (s: string) => void;
  type?: KeyboardType;
}) => {
  const keyboardRef = useRef<SimpleKeyboard>();
  const [layout, setLayout] = useState("default");
  const buttonAttrs = useMemo(
    () => makeButtonAttributes(type, layout),
    [layout, type]
  );
  useEffect(() => {
    setTimeout(() => {
      focusSectionWithPath("/modal");
    }, 500);
  }, []);

  const onKeyPress = useCallback(
    (button: string) => {
      if (button === "{shift}" || button === "{lock}") {
        const currentLayout = keyboardRef.current?.options.layoutName;
        const shiftToggle = currentLayout === "default" ? "shift" : "default";
        setLayout(shiftToggle); // to make a rerender
        keyboardRef.current?.setOptions({
          layoutName: shiftToggle,
        });
      } else if (button === "{enter}") {
        onClose?.();
      }
    },
    [onClose]
  );

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const onKeyPressParent = useCallback((e: React.KeyboardEvent<unknown>) => {
    if (e.key.toLowerCase() === "enter") {
      e.stopPropagation();
      e.preventDefault();
      for (const btn of Object.keys(
        keyboardRef.current?.buttonElements || {}
      )) {
        const btns = keyboardRef.current?.buttonElements[btn];
        if (btns?.find((v) => v === document.activeElement)) {
          keyboardRef.current?.handleButtonClicked(btn);
          break;
        }
      }
    } else if (e.key.match(/\d/)) {
      keyboardRef.current?.handleButtonClicked(e.key);
    }
  }, []);

  return (
    <ModalBase
      isOpen={true}
      style={{
        overlay: { alignItems: "flex-end" },
        content: {
          borderRadius: 0,
          borderTop: "none",
          padding: 0,
          display: "flex",
          flex: 1,
          width: "unset",
        },
      }}
      focusableSectionClassName={styles.section}
      className={styles.container}
      onClose={_onClose}
    >
      <Suspense fallback={null}>
        <div onKeyPress={onKeyPressParent}>
          <KeyboardFactoryWrapper
            keyboardRef={(e) => {
              keyboardRef.current = e;
              keyboardRef.current?.setInput(value || "");
            }}
            physicalKeyboardHighlightPressUsePointerEvents={false}
            stopMouseUpPropagation={true}
            stopMouseDownPropagation={true}
            physicalKeyboardHighlightPreventDefault={true}
            buttonAttributes={buttonAttrs.attributes}
            buttonTheme={buttonAttrs.theme}
            physicalKeyboardHighlightPress={false}
            useButtonTag={true}
            onChange={setValue}
            onKeyPress={onKeyPress}
            layout={getLayout(type)}
            disableButtonHold={true}
          />
        </div>
      </Suspense>
    </ModalBase>
  );
};

export default VirtualKeyboardModal;
export { VirtualKeyboardModal };
